import { Menu } from 'antd';
import { useState, useRef, useEffect } from 'react';
import './NewChatWelcomeScreen.less';
import { getReadonlyElementTitle } from '../../services/subscription';
import { selectIsSubscriptionReadonly } from '../../app/Slices/subscriptionSlice';
import { RootState } from '../../app/store';
import { useSelector } from 'react-redux';
import { useAgentSelector } from '../../hooks/useAgentStore';
import { AppRoutes } from '../../constants/route';
import type { MenuProps } from 'antd';
import { useSearchParams } from 'react-router-dom';
import PromptInput from '../CopilotBase/PromptInput';

interface WelcomeScreenProps {
  onSend: (message: string) => void;
}

type MenuItem = Required<MenuProps>['items'][number];

const NewChatWelcomeScreen = ({ onSend }: WelcomeScreenProps) => {
  const [newPrompt, setNewPrompt] = useState('');
  const [showPromptOptions, setShowPromptOptions] = useState(false);
  const contentEditableRef = useRef<HTMLDivElement>(null);
  const selectedOrgCredential = useSelector(
    (state: RootState) => state.organization.org
  );
  const isReadonly =
    useSelector(selectIsSubscriptionReadonly) || !selectedOrgCredential;
  const promptOptionsRef = useRef<HTMLDivElement>(null);
  const prompts = useAgentSelector((state) => state.promptLibrary.prompts);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (contentEditableRef.current) {
      contentEditableRef.current.focus();
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (
        promptOptionsRef.current &&
        !promptOptionsRef.current.contains(event.target as Node)
      ) {
        setShowPromptOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle prompt from query parameter
  useEffect(() => {
    const promptFromQuery = searchParams.get('prompt');
    if (promptFromQuery && contentEditableRef.current) {
      const decodedPrompt = decodeURIComponent(promptFromQuery);
      contentEditableRef.current.textContent = decodedPrompt;
      setNewPrompt(decodedPrompt);
    }
  }, [searchParams]);

  const handlePromptSelect = (value: string) => {
    if (contentEditableRef.current) {
      contentEditableRef.current.textContent = value;
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(contentEditableRef.current);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
      setNewPrompt(value);
    }
    setShowPromptOptions(false);
  };

  const menuItems: MenuItem[] = prompts.map((prompt) => ({
    key: prompt.key,
    label: prompt.label,
    children: prompt.children.map((child) => ({
      key: child.key,
      label: child.label,
      disabled: child.comingSoon,
      onClick: () => handlePromptSelect(child.value),
    })),
  }));

  return (
    <div id="new-chat-welcome-screen">
      <div className="new-chat-welcome-wrapper">
        <div className="welcome-header">
          <h1>👋 Welcome to {AppRoutes.adminAgent.label}</h1>
          <p>What can I help you with today?</p>
        </div>

        <div className="chat-input-container">
          <PromptInput
            ref={contentEditableRef}
            currentPrompt={newPrompt}
            isLoading={false}
            isReadonly={isReadonly}
            readonlyTooltip={getReadonlyElementTitle()}
            placeholder={
              isReadonly
                ? getReadonlyElementTitle()
                : 'Enter your request or type / to see example prompts'
            }
            onCurrentPromptChange={setNewPrompt}
            onSubmit={(prompt) => onSend(prompt)}
            onShowingPromptTemplateChange={setShowPromptOptions}
          />

          {showPromptOptions && (
            <div className="copilot-form-prompt-options" ref={promptOptionsRef}>
              <Menu
                mode="inline"
                items={menuItems}
                defaultOpenKeys={menuItems
                  .map((item) => item?.key)
                  .filter((key): key is string => key !== undefined)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewChatWelcomeScreen;
